/**
 * The client portal's tabs (BUY1) - the page shows tabs only when the contract declares them.
 *
 * A buy-search contract (`meta.tabs`) names two tabs: the property the owner portal is about, then "Buying" (the
 * off-market buildings that fit a buyer's search). Everything here is a pure rule over the contract's own data: which
 * tabs there are, which tab a link belongs to, and what the property tab is allowed to draw. It never reads the clock,
 * the window or a number.
 *
 * ⛔ A frontend file never imports backend source (it typechecks locally and fails the Vercel build - 2026-09-09), so the
 * gate's rule (`checkPortalTabs` in `backend/src/services/portalBuySearch.ts`: exactly `property` then `buying`, each
 * with a non-empty label) is MIRRORED in `tabsOf`, not imported. If the gate changes, change this too.
 */

export type PortalTabKey = 'property' | 'buying';

export interface PortalTab {
  key: PortalTabKey;
  label: string;
}

/** The sections the Buying tab holds, in the page order. Every other `#section-<key>` belongs to the property tab. */
export const BUYING_SECTION_KEYS = ['buying', 'criteria', 'areas', 'lead', 'deals'] as const;

const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const isSentence = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/**
 * The tabs a contract declares, or null. ⭐ Null unless `meta.tabs` is EXACTLY the gate's valid pair - `property` then
 * `buying`, each with a non-empty label. A stray or malformed `meta.tabs` (one tab, three, reversed, a blank label, an
 * entry that is not an object) never turns a portal into a tabbed one: the page then draws exactly as it did before.
 */
export function tabsOf(deal: { meta?: { tabs?: unknown } } | null | undefined): PortalTab[] | null {
  const tabs = deal?.meta?.tabs;
  if (!Array.isArray(tabs) || tabs.length !== 2) return null;
  const [first, second] = tabs;
  if (!isRecord(first) || !isRecord(second)) return null;
  if (first.key !== 'property' || second.key !== 'buying') return null;
  if (!isSentence(first.label) || !isSentence(second.label)) return null;
  return [
    { key: 'property', label: first.label },
    { key: 'buying', label: second.label },
  ];
}

const DEAL_PREFIX = '#deal-';
const SECTION_PREFIX = '#section-';
const BUYING_KEYS: readonly string[] = BUYING_SECTION_KEYS;

/**
 * Which tab a link's hash belongs to, so a link into the other tab opens it first. `hash` is `window.location.hash`
 * (with its `#`). `#buying` / `#property` name a tab; `#deal-<id>` belongs to Buying when `<id>` is one of the search's
 * buildings; `#section-<k>` belongs to Buying when `<k>` is one of `BUYING_SECTION_KEYS` and to the property tab for any
 * other section. Anything else - an unknown building, another anchor - and any contract without tabs is null: the link
 * is left to scroll where it points.
 */
export function tabForHash(hash: string, tabs: PortalTab[] | null, buyingDealIds: ReadonlySet<string>): PortalTabKey | null {
  if (!tabs) return null;
  if (hash === '#buying') return 'buying';
  if (hash === '#property') return 'property';
  if (hash.startsWith(DEAL_PREFIX)) return buyingDealIds.has(hash.slice(DEAL_PREFIX.length)) ? 'buying' : null;
  if (hash.startsWith(SECTION_PREFIX)) return BUYING_KEYS.includes(hash.slice(SECTION_PREFIX.length)) ? 'buying' : 'property';
  return null;
}

/**
 * What a tab is allowed to draw. The property tab is the owner portal as it has always been: it gets a COPY of the deal
 * without `pipeline` and `buyBox`, so nothing of the search can reach it by any route (a section menu, a print chooser,
 * a map). The Buying tab, and a page with no tabs, get the deal itself. The input is never changed.
 */
export function projectForTab<D extends { pipeline?: unknown; buyBox?: unknown }>(deal: D, tab: PortalTabKey | null): D {
  if (tab !== 'property') return deal;
  const { pipeline: _pipeline, buyBox: _buyBox, ...rest } = deal;
  return rest as unknown as D;
}
